import { ChevronRightIcon, ScaleIcon } from '@heroicons/react/24/outline';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import { Skeleton } from '@/shared/ui/skeleton/Skeleton';
import { Tooltip } from '@/shared/ui/tooltip/Tooltip';
import { Text } from '../text';

type AiMenuItemProps = ComponentProps<typeof Button> & {
  menuTitle?: string;
  description?: string;
  icon?: ReactNode;
  helpTooltip?: string;
  isLoading?: boolean;
  statusSlot?: ReactNode;
};

/**
 * AI機能メニュー項目コンポーネント
 *
 * Figmaデザイン準拠のAI機能選択用メニュー項目。
 * Buttonベースで実装され、ホバー時にグラデーション背景とアイコン色が変化する。
 *
 * @param menuTitle - メニュー項目のタイトル
 * @param description - メニュー項目の説明
 * @param icon - 左側に表示するアイコン（デフォルト: ScaleIcon）
 * @param helpTooltip - ヘルプアイコンのツールチップテキスト
 * @param isLoading - ローディング状態（trueの場合Skeleton表示）
 * @param statusSlot - 説明文の下に表示するステータス要素
 */
export const AiMenuItem = ({
  menuTitle = 'title',
  description = 'description',
  icon,
  helpTooltip,
  isLoading,
  statusSlot,
  className,
  ...props
}: AiMenuItemProps) => {
  if (isLoading) {
    return (
      <div
        className={cn(
          'relative flex w-full items-center gap-3 rounded-12 border p-4 pl-[64px]',
          className,
        )}
      >
        <Skeleton className="absolute top-1/2 left-[15px] size-10 -translate-y-1/2 rounded-10" />
        <div className="flex flex-1 flex-col gap-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
    );
  }

  return (
    <Button
      size="none"
      variant="outline"
      className={cn(
        'relative w-full items-center justify-between gap-3 rounded-12 p-4 pl-[64px]',
        'hover:border-primary hover:bg-gradient-to-r hover:from-[2.344%] hover:from-[rgba(240,253,248,0.3)] hover:to-[99.644%] hover:to-[rgba(221,249,236,0.3)]',
        className,
      )}
      {...props}
    >
      {/* アイコン背景付きコンテナ */}
      <div
        className={cn(
          'absolute top-1/2 left-[15px] -translate-y-1/2 rounded-10 p-2',
          'rounded-[10px] bg-surface-accent',
        )}
      >
        {icon || <ScaleIcon className="text-text-accent" />}
      </div>

      {/* Content */}
      <div className="flex min-h-px min-w-px flex-1 flex-col gap-1">
        <div className="flex w-full items-center gap-1">
          <Text variant="subhead" className="transition-colors group-hover:text-primary">
            {menuTitle}
          </Text>
          {helpTooltip && (
            <Tooltip tooltipContent={helpTooltip} contentClassName="max-w-[300px]">
              <div>
                <QuestionMarkCircleIcon className="size-4 shrink-0 text-secondary group-hover:text-secondary" />
              </div>
            </Tooltip>
          )}
        </div>
        <Text variant="description" className="whitespace-normal text-left">
          {description}
        </Text>
        {statusSlot && <div className="self-start">{statusSlot}</div>}
      </div>

      <ChevronRightIcon className="size-5 text-secondary transition-colors group-hover:text-primary" />
    </Button>
  );
};
